import React, { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, BellRing, BellOff, Volume2, VolumeX, Moon } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { enableAdminPush, disableAdminPush, getAdminPushState } from "../lib/adminPush";
import { isSoundEnabled, setSoundEnabled, playNotifSound } from "../lib/notifSound";
import OrderEtaDialog, { fmtReadyAt } from "./OrderEtaDialog";

const SOUND_TYPES = new Set(["new_order", "customer_reply", "new_reservation", "order_late"]);

const ICON = { kpi_drop: "📉", kpi_spike: "🚀", weekly_summary: "📊", customer_reply: "💬", new_order: "🛍️", new_reservation: "🎁", evening_digest: "🌙", campaign_auto_launch: "⏱️", campaign_auto_launch_error: "❌", order_late: "⏰", backup_failed: "🛟" };
const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// Header bell with unread admin notifications (KPI drops, auto-launched campaigns)
export default function AdminNotifBell({ className = "", variant = "desktop" }) {
  const { user } = useAuth();
  const [data, setData] = useState({ unread: 0, notifications: [] });
  const [open, setOpen] = useState(false);
  const [pushState, setPushState] = useState("off");
  const [sound, setSound] = useState(isSoundEnabled());
  const seenRef = useRef(null); // created_at della notifica più recente vista
  const isAdmin = user?.role === "admin";
  const enabled = !!user; // staff: solo ordini/prenotazioni della propria sede (filtro lato server)
  const navigate = useNavigate();
  const [acting, setActing] = useState(null);
  const [etaFor, setEtaFor] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [quick, setQuick] = useState([]);
  useEffect(() => { if (isAdmin && open) api.get("/quick-replies").then((r) => setQuick((r.data.items || []).slice(0, 3))).catch(() => {}); }, [isAdmin, open]);
  const [waReady, setWaReady] = useState(false);
  useEffect(() => { if (isAdmin && open) api.get("/customer-messages/wa-ready").then((r) => setWaReady(!!r.data?.ready)).catch(() => setWaReady(false)); }, [isAdmin, open]);

  useEffect(() => {
    if (!enabled) return;
    const load = () => api.get("/admin/notifications", { params: { limit: 15 } }).then((r) => {
      setData(r.data);
      const list = r.data.notifications || [];
      if (seenRef.current !== null) {
        const fresh = list.filter((n) => !n.read_at && n.created_at > seenRef.current && SOUND_TYPES.has(n.type));
        if (fresh.length && variant === "desktop" && !r.data.quiet_now) playNotifSound();
      }
      if (list[0]) seenRef.current = list[0].created_at > (seenRef.current || "") ? list[0].created_at : seenRef.current;
      else if (seenRef.current === null) seenRef.current = "";
    }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [enabled, variant]);

  useEffect(() => { if (enabled) getAdminPushState().then(setPushState); }, [enabled]);

  const togglePush = async () => {
    try {
      if (pushState === "on") { await disableAdminPush(); setPushState("off"); toast.success("Push disattivate su questo dispositivo"); }
      else { await enableAdminPush(); setPushState("on"); toast.success("Push attive: riceverai risposte clienti e alert"); }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Impossibile attivare le push"); setPushState(await getAdminPushState()); }
  };

  useEffect(() => {
    if (!isAdmin) return;
    const load = () => api.get("/admin/notifications", { params: { limit: 15 } }).then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isAdmin]);

  if (!enabled) return null;

  const markOne = async (n) => {
    if (n.read_at) return;
    try {
      await api.post(`/admin/notifications/${n.id}/read`);
      setData((d) => ({ unread: Math.max(0, d.unread - 1), notifications: d.notifications.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)) }));
    } catch {}
  };

  const markReady = async (n) => {
    setActing(n.id);
    try {
      await api.patch(`/reservations/${n.reservation_id}`, { status: "ready" });
      await markOne(n);
      setData((d) => ({ ...d, notifications: d.notifications.map((x) => (x.id === n.id ? { ...x, action_done: "ready" } : x)) }));
      toast.success("Prenotazione segnata come pronta · il cliente riceve l'avviso");
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossibile aggiornare la prenotazione"); }
    finally { setActing(null); }
  };

  const ORDER_LABEL = { in_prep: "In preparazione", ready: "Pronto" };
  const setOrderStatus = async (n, status, etaMinutes = null) => {
    setActing(n.id);
    try {
      const payload = { status };
      if (status === "in_prep" && etaMinutes) payload.eta_minutes = etaMinutes;
      const { data } = await api.patch(`/customer-orders/${n.order_id}`, payload);
      await markOne(n);
      setData((d) => ({ ...d, notifications: d.notifications.map((x) => (x.id === n.id ? { ...x, action_done: status, ready_at: data?.ready_at } : x)) }));
      const at = data?.ready_at ? ` · pronto per le ${fmtReadyAt(data.ready_at)}` : "";
      toast.success(status === "ready" ? "Ordine segnato pronto · il cliente riceve l'avviso"
        : n.reschedule ? `Nuovo orario inviato al cliente con le scuse${at}` : `Ordine segnato in preparazione${at}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossibile aggiornare l'ordine"); }
    finally { setActing(null); setEtaFor(null); }
  };

  const goReply = async (n) => {
    await markOne(n);
    setOpen(false);
    navigate(`/messaggi?customer=${n.customer_id}`);
  };

  const fillQuick = (n, q) => {
    const name = ((n.title || "").replace(/^💬\s*/, "").split(" ha scritto")[0] || "").trim().split(" ")[0];
    return q.text.replace(/{nome}/g, name || "").replace(/\bin {indirizzo}/g, "nella nostra sede").replace(/{indirizzo}/g, "la nostra sede").replace(/\s{2,}/g, " ").trim();
  };
  const sendInlineReply = async (n, textOverride) => {
    const text = (textOverride ?? replyText).trim();
    if (!text) return;
    setActing(n.id);
    try {
      const { data: sent } = await api.post("/customer-messages/reply", { customer_id: n.customer_id, body: text, via_whatsapp: waReady });
      await markOne(n);
      setData((d) => ({ ...d, notifications: d.notifications.map((x) => (x.id === n.id ? { ...x, action_done: "replied" } : x)) }));
      setReplyFor(null); setReplyText("");
      if (sent.channel === "whatsapp" && sent.spoki_state !== "accepted") toast.warning(`Risposta salvata nel portale, WhatsApp non inviato (${sent.spoki_reason || sent.spoki_state})`);
      else toast.success(sent.channel === "whatsapp" ? "Risposta inviata al cliente su WhatsApp" : "Risposta inviata nel portale cliente");
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossibile inviare la risposta"); }
    finally { setActing(null); }
  };

  const goOpen = async (n) => {
    await markOne(n);
    setOpen(false);
    navigate(n.url || "/");
  };

  const toggleSound = () => { const nx = !sound; setSound(nx); setSoundEnabled(nx); if (nx) playNotifSound(); };

  const markAll = async () => {
    try {
      await api.post("/admin/notifications/read-all");
      setData((d) => ({ unread: 0, notifications: d.notifications.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })) }));
    } catch {}
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`admin-notif-bell-${variant}`}
          title="Notifiche admin"
          className={`relative p-2 rounded-md bg-zinc-800/60 hover:bg-zinc-700/70 transition ${className}`}
        >
          <Bell className={`h-5 w-5 ${data.unread > 0 ? "text-amber-300" : "text-zinc-300"}`} />
          {data.unread > 0 && (
            <span data-testid={`admin-notif-bell-badge-${variant}`} className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--paprika)] text-white text-[10px] font-bold flex items-center justify-center">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 sm:w-96 p-0 bg-zinc-950 border-zinc-800" data-testid="admin-notif-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
          <span className="text-sm font-heading font-semibold flex items-center gap-1.5">Notifiche admin
            {data.quiet_now && <span data-testid="admin-notif-quiet-badge" title="Fascia Non disturbare attiva: push e suoni silenziati" className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-700/50 flex items-center gap-1"><Moon className="h-3 w-3" /> Silenzio</span>}
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={toggleSound} data-testid="admin-sound-toggle" title={sound ? "Disattiva suono nuovi ordini/messaggi" : "Attiva suono nuovi ordini/messaggi"}
              className={`p-1 rounded-full border ${sound ? "border-emerald-700/60 text-emerald-300" : "border-zinc-700 text-zinc-500 hover:text-zinc-300"}`}>
              {sound ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />}
            </button>
            {pushState !== "unsupported" && (
              <button type="button" onClick={togglePush} data-testid="admin-push-toggle" disabled={pushState === "denied"}
                title={pushState === "denied" ? "Permesso notifiche negato nel browser" : pushState === "on" ? "Disattiva push su questo dispositivo" : "Ricevi push su questo dispositivo"}
                className={`text-[11px] flex items-center gap-1 px-2 py-0.5 rounded-full border ${pushState === "on" ? "border-emerald-700/60 text-emerald-300 bg-emerald-500/10" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"} disabled:opacity-40`}>
                {pushState === "on" ? <BellRing className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
                {pushState === "on" ? "Push attive" : "Attiva push"}
              </button>
            )}
            {data.unread > 0 && (
              <button type="button" onClick={markAll} data-testid="admin-notif-mark-all" className="text-[11px] text-indigo-300 hover:text-indigo-200 flex items-center gap-1">
                <CheckCheck className="h-3.5 w-3.5" /> Segna lette
              </button>
            )}
          </div>
        </div>
        <ul className="max-h-80 overflow-y-auto divide-y divide-zinc-800/60" data-testid="admin-notif-list">
          {data.notifications.length === 0 && (
            <li className="p-4 text-xs text-zinc-500 text-center">Nessuna notifica</li>
          )}
          {data.notifications.map((n) => (
            <li key={n.id} data-testid={`admin-notif-item-${n.id}`} className={`px-3 py-2 text-xs ${n.read_at ? "text-zinc-500" : "bg-indigo-500/10 text-zinc-200"}`}>
              <div className="flex items-start gap-2">
                <span className="text-base leading-none">{ICON[n.type] || "🔔"}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{n.title}</div>
                  {n.body && <div className="text-[11px] opacity-80 line-clamp-2">{n.body}</div>}
                  <div className="text-[10px] opacity-60 mt-0.5">{fmt(n.created_at)}</div>
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`admin-notif-actions-${n.id}`}>
                    {n.type === "new_reservation" && n.reservation_id && (
                      n.action_done === "ready"
                        ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-700/50">✓ Pronto</span>
                        : <button type="button" disabled={acting === n.id} onClick={() => markReady(n)} data-testid={`notif-action-ready-${n.id}`}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600/80 hover:bg-emerald-500 text-white disabled:opacity-50">🎁 Segna pronto</button>
                    )}
                    {n.type === "customer_reply" && n.customer_id && isAdmin && (
                      n.action_done === "replied"
                        ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-700/50" data-testid={`notif-replied-${n.id}`}>✓ Risposto</span>
                        : <>
                          <button type="button" onClick={() => { setReplyFor(replyFor === n.id ? null : n.id); setReplyText(""); }} data-testid={`notif-action-reply-${n.id}`}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-600/80 hover:bg-indigo-500 text-white">💬 Rispondi</button>
                          <button type="button" onClick={() => goReply(n)} data-testid={`notif-action-open-chat-${n.id}`}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-zinc-700 text-zinc-300 hover:text-white">Apri chat</button>
                          {n.url && <button type="button" onClick={() => goOpen(n)} data-testid={`notif-action-open-card-${n.id}`}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-zinc-700 text-zinc-300 hover:text-white">Scheda</button>}
                        </>
                    )}
                    {n.type === "new_order" && n.order_id && (() => {
                      const st = n.action_done || n.order_status;
                      const readyAt = n.ready_at || n.order_ready_at;
                      if (["ready", "delivered"].includes(st)) return <span data-testid={`notif-order-done-${n.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-700/50">✓ Pronto</span>;
                      if (st === "cancelled") return <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">Annullato</span>;
                      return (
                        <>
                          {st === "in_prep"
                            ? <span data-testid={`notif-order-inprep-${n.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-700/50">✓ {ORDER_LABEL.in_prep}{readyAt ? ` · ${fmtReadyAt(readyAt)}` : ""}</span>
                            : <button type="button" disabled={acting === n.id} onClick={() => setEtaFor(n)} data-testid={`notif-action-inprep-${n.id}`}
                                className="text-[10px] px-2 py-0.5 rounded-full bg-sky-600/80 hover:bg-sky-500 text-white disabled:opacity-50">👨‍🍳 In preparazione</button>}
                          <button type="button" disabled={acting === n.id} onClick={() => setOrderStatus(n, "ready")} data-testid={`notif-action-order-ready-${n.id}`}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600/80 hover:bg-emerald-500 text-white disabled:opacity-50">✅ Pronto</button>
                        </>
                      );
                    })()}
                    {n.type === "new_order" && (
                      <button type="button" onClick={() => goOpen({ ...n, url: "/ordini-cliente" })} data-testid={`notif-action-order-${n.id}`}
                        className="text-[10px] px-2 py-0.5 rounded-full border border-amber-700/60 text-amber-300 hover:text-amber-100">🛍️ Apri ordini</button>
                    )}
                    {n.type === "order_late" && n.order_id && (() => {
                      const st = n.action_done || n.order_status;
                      const rescheduled = n.action_done === "in_prep" || (n.order_rescheduled_count > 0 && n.order_ready_at && n.order_ready_at > n.created_at);
                      const readyAt = n.ready_at || n.order_ready_at;
                      if (["ready", "delivered"].includes(st)) return <span data-testid={`notif-order-done-${n.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-700/50">✓ Pronto</span>;
                      if (st === "cancelled") return <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">Annullato</span>;
                      return (
                        <>
                          <button type="button" disabled={acting === n.id} onClick={() => setOrderStatus(n, "ready")} data-testid={`notif-action-late-ready-${n.id}`}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600/80 hover:bg-emerald-500 text-white disabled:opacity-50">✅ Segna pronto</button>
                          {rescheduled
                            ? <span data-testid={`notif-late-rescheduled-${n.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-700/50">🙏 Scuse inviate · {readyAt ? fmtReadyAt(readyAt) : ""}</span>
                            : <button type="button" disabled={acting === n.id} onClick={() => setEtaFor({ ...n, reschedule: true, ready_at: n.order_ready_at })} data-testid={`notif-action-late-reschedule-${n.id}`}
                                className="text-[10px] px-2 py-0.5 rounded-full bg-sky-600/80 hover:bg-sky-500 text-white disabled:opacity-50">🕒 Nuovo orario</button>}
                          <button type="button" onClick={() => goOpen({ ...n, url: "/ordini-cliente" })} data-testid={`notif-action-late-open-${n.id}`}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-red-700/60 text-red-300 hover:text-red-100">🛍️ Apri ordini</button>
                        </>
                      );
                    })()}
                    {!["new_reservation", "customer_reply", "new_order", "order_late"].includes(n.type) && n.url && n.url !== "/" && (
                      <button type="button" onClick={() => goOpen(n)} data-testid={`notif-action-open-${n.id}`}
                        className="text-[10px] px-2 py-0.5 rounded-full border border-zinc-700 text-zinc-300 hover:text-white">Apri →</button>
                    )}
                  </div>
                  {n.type === "customer_reply" && replyFor === n.id && (
                    <div className="mt-2 space-y-1.5" data-testid={`notif-reply-box-${n.id}`} onClick={(e) => e.stopPropagation()}>
                      {quick.length > 0 && (
                        <div className="flex flex-wrap gap-1" data-testid={`notif-quick-replies-${n.id}`}>
                          {quick.map((q) => (
                            <button key={q.id} type="button" disabled={acting === n.id} onClick={() => sendInlineReply(n, fillQuick(n, q))} title={fillQuick(n, q)} data-testid={`notif-quick-${q.id}-${n.id}`}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 hover:bg-indigo-600/70 text-zinc-200 border border-zinc-700 disabled:opacity-50">⚡ {q.title || q.text.slice(0, 18)}</button>
                          ))}
                        </div>
                      )}
                      <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2} autoFocus placeholder="Scrivi la risposta…"
                        className="w-full rounded-md bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-indigo-500" data-testid={`notif-reply-text-${n.id}`}
                        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendInlineReply(n); }} />
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-zinc-500">{waReady ? "Inviata su WhatsApp e nel portale" : "Inviata nel portale cliente (WhatsApp non attivo)"}</span>
                        <button type="button" disabled={acting === n.id || !replyText.trim()} onClick={() => sendInlineReply(n)} data-testid={`notif-reply-send-${n.id}`}
                          className="text-[10px] px-3 py-1 rounded-full bg-emerald-600/90 hover:bg-emerald-500 text-white disabled:opacity-50">Invia</button>
                      </div>
                    </div>
                  )}
                </div>
                {!n.read_at && <span className="h-2 w-2 rounded-full bg-indigo-400 mt-1 shrink-0" />}
              </div>
            </li>
          ))}
        </ul>
        <div className="px-3 py-2 border-t border-zinc-800 text-center">
          {isAdmin
            ? <Link to="/notifiche" onClick={() => setOpen(false)} data-testid="admin-notif-see-all" className="text-[11px] text-indigo-300 hover:text-indigo-200">Vedi storico e preferenze →</Link>
            : <span className="text-[10px] text-zinc-500">Ordini e prenotazioni della tua sede</span>}
        </div>
      </PopoverContent>
      <OrderEtaDialog order={etaFor} mode={etaFor?.reschedule ? "reschedule" : "start"} busy={!!acting} onClose={() => setEtaFor(null)} onConfirm={(m) => setOrderStatus(etaFor, "in_prep", m)} />
    </Popover>
  );
}
